import { createContext, PropsWithChildren, useMemo, ReactElement, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useContentQuery } from 'app/data/queries/use-content-query';
import { TenantContentData } from 'app/data/types/content';

interface ContentContextValue {
  content: TenantContentData | undefined;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
}

const ContentContext = createContext<ContentContextValue | undefined>(undefined);

export const ContentProvider = ({
  children,
}: PropsWithChildren): ReactElement => {
  const { pathname } = useLocation();
  const contentUuid = pathname.split('/').pop();

  const { data: content, isLoading, isError, isSuccess } = useContentQuery(contentUuid ?? '');

  const contextValue = useMemo(() => ({ content, isLoading, isError, isSuccess }), [content, isLoading, isError, isSuccess]);

  return <ContentContext.Provider value={contextValue}>{children}</ContentContext.Provider>;
};

export function useContent(): ContentContextValue {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
}
